/* Platform detection for the install gate.
 *
 * The rule we enforce: on a touch device - phone OR tablet, Android or iPad
 * alike - the app may only run as an installed PWA, so that browser tab gets
 * blocked and shown install instructions instead. Desktops and laptops are
 * left alone; the web app is the right experience there.
 */

export function isStandalone(): boolean {
  // display-mode covers Android/Chrome and desktop installs.
  const byDisplayMode = ['standalone', 'fullscreen', 'minimal-ui']
    .some((m) => window.matchMedia(`(display-mode: ${m})`).matches)

  // iOS Safari predates display-mode and uses a non-standard flag.
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true

  // Launched from the manifest start_url / shortcuts.
  const fromManifest = new URLSearchParams(window.location.search)
    .get('source')?.startsWith('pwa') ?? false

  return byDisplayMode || iosStandalone || fromManifest
}

/** True on any touch-primary device: phone or tablet, Android or Apple. */
export function isMobileDevice(): boolean {
  const ua = navigator.userAgent

  // Phones, plus Android tablets - an Android tablet UA still carries
  // "Android" (it just drops the "Mobile" token a phone adds).
  const uaPhone = /Android|iPhone|iPod|Opera Mini|IEMobile|BlackBerry|webOS/i.test(ua)
  const uaTablet = /iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)

  // iPadOS 13+ (and any tablet set to "request desktop site") reports a
  // desktop UA, so the user-agent string alone misses them. A touch-PRIMARY
  // pointer is the reliable tell: a real desktop reports `pointer: fine`,
  // and so does a touchscreen laptop, because it also has a mouse. Only a
  // device whose main input is a finger reports coarse.
  // `> 0`, not `> 1`: a touch device reports at least one touch point, and
  // some report exactly one. The coarse-pointer half of the test is what
  // keeps desktops out - a touchscreen laptop still reports `pointer: fine`
  // because it also has a mouse.
  const touchPrimary =
    navigator.maxTouchPoints > 0 &&
    window.matchMedia('(pointer: coarse)').matches

  // iPadOS specifically: Safari there claims to be macOS, and no real Mac
  // has a touchscreen (a trackpad reports zero touch points), so "Macintosh
  // with touch points" is an iPad.
  const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 0

  // A desktop operating system is never gated on the strength of the touch
  // heuristic alone: a Windows/Linux/ChromeOS machine with a touchscreen is
  // a laptop, and forcing its owner to install an app would be wrong. Those
  // platforms still get gated if the UA itself says tablet (rare), which is
  // why this only suppresses `touchPrimary`.
  const desktopOs = /Windows NT|X11|CrOS|Linux x86_64/.test(ua) && !/Android/i.test(ua)

  return uaPhone || uaTablet || iPadOs || (touchPrimary && !desktopOs)
}

/** True for a tablet rather than a phone - the install steps differ slightly
 * (Safari's share button sits in the top toolbar on iPad, not the bottom
 * bar), and the copy should not tell a tablet owner "on a phone". */
export function isTablet(): boolean {
  if (!isMobileDevice()) return false
  const ua = navigator.userAgent
  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return true
  // iPadOS masquerading as macOS.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 0) return true
  // Android: phones carry the "Mobile" token, tablets don't.
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true
  // Fall back on the shorter screen edge - phones stay under ~600px.
  return Math.min(window.screen.width, window.screen.height) >= 600
}

export function isIos(): boolean {
  const ua = navigator.userAgent
  return /iPhone|iPod|iPad/i.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 0)
}

/** True when the browser should be blocked in favour of the installed app.
 *
 * Phones and tablets are BLOCKED until the app is installed. A computer is
 * not: the web app is a first-class experience there, so desktops get the
 * dismissible InstallPrompt card instead of this gate - every device can
 * install, but only handheld ones must. (A desktop block was tried and
 * reverted: it locked people out of perfectly good browsers, and Firefox
 * cannot install a PWA at all.) */
export function shouldGateToPwa(): boolean {
  return isMobileDevice() && !isStandalone()
}

/* Chrome fires beforeinstallprompt before React mounts, so it is captured at
 * module load and replayed when the gate asks for it. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: InstallPromptEvent | null = null
const listeners = new Set<(available: boolean) => void>()

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredPrompt = e as InstallPromptEvent
  listeners.forEach((fn) => fn(true))
})

window.addEventListener('appinstalled', () => {
  deferredPrompt = null
  listeners.forEach((fn) => fn(false))
})

export function hasInstallPrompt(): boolean {
  return deferredPrompt !== null
}

export function onInstallAvailability(fn: (available: boolean) => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export async function triggerInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable'
  await deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice
  if (outcome === 'accepted') deferredPrompt = null
  return outcome
}
